import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState } from "react";
import API from "../Network/API";
import { currentLanguage } from "./locale";

// Prices are stored and charged in USD; other currencies are display conversions.
export const BASE_CURRENCY = "USD";
// The visitor's display currency: set from their location on the first visit, then by the header picker.
export const CURRENCY_KEY = "xller_currency";

export function storedCurrency() {
    try {
        return localStorage.getItem(CURRENCY_KEY);
    } catch (e) {
        return null;
    }
}

export function storeCurrency(code) {
    try {
        localStorage.setItem(CURRENCY_KEY, code);
    } catch (e) {}
}

const CurrencyContext = createContext({
    currency: BASE_CURRENCY,
    currencies: [],
    setCurrency: () => {},
    formatPrice: (usd) => `$${usd}`,
});

export function CurrencyProvider({ children }) {
    const [currency, setCurrencyState] = useState(() => storedCurrency() || BASE_CURRENCY);
    const [currencies, setCurrencies] = useState([{ code: BASE_CURRENCY, name: "US Dollar", rate: 1 }]);

    useEffect(() => {
        API.get("/v1/oauth/currency-rates")
            .then((response) => {
                const list = response.data?.data?.currencies;
                if (Array.isArray(list) && list.length) setCurrencies(list);
            })
            .catch(() => {});
    }, []);

    const setCurrency = useCallback((code) => {
        storeCurrency(code);
        setCurrencyState(code);
    }, []);

    const rates = useMemo(() => Object.fromEntries(currencies.map((c) => [c.code, Number(c.rate)])), [currencies]);
    // An unknown or not-yet-loaded rate falls back to USD rather than showing a wrong number.
    const active = rates[currency] ? currency : BASE_CURRENCY;

    // USD amount -> "$7", "£5.29", "AED 25.71", "PKR 1,950".
    const formatPrice = useCallback((usd) => {
        const amount = Number(usd);
        if (!Number.isFinite(amount)) return "";
        const value = amount * (rates[active] || 1);
        const whole = active === BASE_CURRENCY ? Number.isInteger(value) : value >= 100;
        const lang = currentLanguage() === "ar" ? "ar-u-nu-latn" : "en-US";
        try {
            return new Intl.NumberFormat(lang, {
                style: "currency",
                currency: active,
                minimumFractionDigits: whole ? 0 : undefined,
                maximumFractionDigits: whole ? 0 : 2,
            }).format(whole ? Math.round(value) : value);
        } catch (e) {
            return `${active} ${value.toFixed(2)}`;
        }
    }, [active, rates]);

    const value = useMemo(
        () => ({ currency: active, currencies, setCurrency, formatPrice }),
        [active, currencies, setCurrency, formatPrice]
    );

    return createElement(CurrencyContext.Provider, { value }, children);
}

export function useCurrency() {
    return useContext(CurrencyContext);
}
